import { Controller } from "@hotwired/stimulus"

// Client-side search + status filter + alphabetical sort for the settings lists
// (timesheet templates, custom report types). Filters <li> items by their
// data-name and data-active attributes (and data-subject where the list has a
// subject filter: Mitarbeiter- vs. Fahrzeug-Vorlagen), and reorders them in
// place, cycling through the original (position) order, A-Z and Z-A.
export default class extends Controller {
  static targets = ["item", "empty", "sortLabel", "status", "subject"]
  static values = { labels: Object }

  connect() {
    // Remember the server-provided (position) order so it can be restored.
    this.originalOrder = [...this.itemTargets]
    this.sortState = "position"
    // The server renders every item; adopt the status select's initial value
    // (default "active") so inactive rows are hidden on first render. Lists
    // without a status filter fall back to "all" so every item stays visible.
    this.query = ""
    this.status = this.hasStatusTarget ? this.statusTarget.value : "all"
    this.subject = this.hasSubjectTarget ? this.subjectTarget.value : "all"
    this.applyFilters()
  }

  search(event) {
    this.query = event.target.value.toLowerCase().trim()
    this.applyFilters()
  }

  filterStatus(event) {
    this.status = event.target.value
    this.applyFilters()
  }

  filterSubject(event) {
    this.subject = event.target.value
    this.applyFilters()
  }

  applyFilters() {
    let visibleCount = 0

    this.itemTargets.forEach((item) => {
      const matchesQuery = this.query === "" || (item.dataset.name || "").includes(this.query)
      const matchesStatus = this.statusMatches(item)
      const visible = matchesQuery && matchesStatus && this.subjectMatches(item)
      item.classList.toggle("hidden", !visible)
      if (visible) visibleCount += 1
    })

    if (this.hasEmptyTarget) {
      this.emptyTarget.classList.toggle("hidden", visibleCount > 0)
    }
  }

  // "active"/"inactive" match on the data-active attribute; anything else
  // (e.g. "all", or a deselected/empty value) shows every item.
  statusMatches(item) {
    if (this.status === "active") return item.dataset.active === "true"
    if (this.status === "inactive") return item.dataset.active === "false"
    return true
  }

  // "all" (or a deselected/empty value) shows every item; otherwise the item's
  // data-subject ("employee"/"fleet") must match.
  subjectMatches(item) {
    if (!this.subject || this.subject === "all") return true
    return item.dataset.subject === this.subject
  }

  toggleSort() {
    const next = { position: "asc", asc: "desc", desc: "position" }
    this.sortState = next[this.sortState] || "asc"

    const ordered =
      this.sortState === "position"
        ? this.originalOrder
        : [...this.itemTargets].sort((a, b) => {
            const cmp = (a.dataset.name || "").localeCompare(b.dataset.name || "")
            return this.sortState === "asc" ? cmp : -cmp
          })

    const list = this.itemTargets[0]?.parentElement
    if (list) ordered.forEach((item) => list.appendChild(item))

    if (this.hasSortLabelTarget && this.hasLabelsValue) {
      this.sortLabelTarget.textContent = this.labelsValue[this.sortState]
    }
  }
}
